const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const RESPONSE_PLATE_KEYS = [
  'formatted_plate', 'formattedPlate',
  'plate', 'licensePlate', 'license_plate', 'plateNumber', 'plate_number',
  'plateText', 'plate_text', 'raw_text', 'rawText',
  'recognizedPlate', 'recognized_plate', 'text', 'result', 'details',
];

const toPersianDigits = value => String(value ?? '')
  .replace(/[0-9]/g, digit => PERSIAN_DIGITS[Number(digit)])
  .replace(/[٠-٩]/g, digit => PERSIAN_DIGITS['٠١٢٣٤٥٦٧٨٩'.indexOf(digit)]);

const pick = (object, keys) => keys.map(key => object?.[key]).find(value => value !== undefined && value !== null && value !== '');

const normalizeCharacters = value => toPersianDigits(value)
  .replace(/[يى]/g, 'ی')
  .replace(/ك/g, 'ک');

const onlyDigits = value => normalizeCharacters(value).replace(/[^۰-۹]/g, '');

const buildPlate = (part1, letter, part2, end) => {
  const first = onlyDigits(part1);
  const middle = onlyDigits(part2);
  const last = onlyDigits(end);
  const normalizedLetter = normalizeCharacters(letter).match(/[^۰-۹\s|:_-]/)?.[0] || '';
  if (first.length !== 2 || middle.length !== 3 || last.length !== 2 || !normalizedLetter) return '';
  return `${first} ${normalizedLetter} ${middle} | ${last}`;
};

export function formatPlateForForm(value) {
  if (!value) return '';

  if (typeof value === 'object') {
    const part1 = pick(value, ['plate1', 'part1', 'first', 'twoDigit', 'left_two']);
    const letter = pick(value, ['plate3', 'letter', 'character']);
    const part2 = pick(value, ['plate2', 'part2', 'middle', 'threeDigit', 'middle_three']);
    const end = pick(value, ['plateEnd', 'part3', 'end', 'iranCode', 'province', 'province_code']);
    if (part1 && letter && part2 && end) {
      return buildPlate(part1, letter, part2, end);
    }
  }

  // OCR services often return labels/confidence around the actual value,
  // e.g. "plate: 12 ب 345 ایران 67 (98%)". Search for the plate-shaped
  // fragment instead of treating the entire response as the plate.
  const normalized = normalizeCharacters(value);
  const match = normalized.match(/([۰-۹]{2})\s*([^۰-۹\s|:_-])\s*([۰-۹]{3})\s*(?:ایران\s*)?([۰-۹]{2})/);
  return match ? buildPlate(match[1], match[2], match[3], match[4]) : '';
}

export function extractPlateFromResponse(response) {
  const visited = new WeakSet();
  const findPlate = (payload, depth = 0) => {
    if (payload === null || payload === undefined || depth > 7) return '';
    if (typeof payload !== 'object') return formatPlateForForm(payload);
    if (visited.has(payload)) return '';
    visited.add(payload);

    const directParts = formatPlateForForm(payload);
    if (directParts) return directParts;

    // The backend can return multiple detections. Prefer a plate explicitly
    // validated by the Python service, then fall back to the first readable
    // candidate so older responses still work.
    if (Array.isArray(payload)) {
      const ordered = [...payload].sort((a, b) => Number(b?.is_valid === true) - Number(a?.is_valid === true));
      for (const item of ordered) {
        const found = findPlate(item, depth + 1);
        if (found) return found;
      }
      return '';
    }

    for (const key of RESPONSE_PLATE_KEYS) {
      if (payload[key] !== undefined) {
        const found = findPlate(payload[key], depth + 1);
        if (found) return found;
      }
    }
    for (const value of Object.values(payload)) {
      const found = findPlate(value, depth + 1);
      if (found) return found;
    }
    return '';
  };
  return findPlate(response?.data ?? response);
}
